<template>
  <div>
    <v-skeleton-loader
      type="paragraph"
      class="pt-2 pb-4 mx-3"
      v-if="isLoading"
    />
    <MessageComposer
      v-show="!isLoading"
      autofocus
      dense
      flat
      solo
      v-bind="$attrs"
      submitButtonTestId="recognitionComposer-button-send"
      :submitButtonText="submitButtonText"
      :rows="rows"
      :accountSuggestions="filteredAccountSuggestions"
      :initialMentionedAccounts="initialMentionedAccounts"
      :initialMessage="initialContent"
      :isSearchingAccounts="isSearchingAccounts"
      :placeholder="$t('recognitions.recognition_placeholder')"
      @searchAccounts="searchAccounts"
      @submit="handleSubmit"
    >
      <template v-slot:additional-actions>
        <v-btn
          v-if="showCancel"
          text
          color="blue-grey darken-1"
          class="me-4"
          rounded
          data-testID="recognitionComposer-button-cancel"
          @click="$emit('cancel')"
          >{{ $t('common.button.cancel') }}</v-btn
        >
      </template>
    </MessageComposer>
  </div>
</template>
<script>
import api from '@/api'
import mixin from '@/mixins/mixin.vue'

import _ from 'lodash'

import MessageComposer from '@/components/MessageComposer.vue'
import { mapGetters } from 'vuex'

const DEFAULT_SORT = 'createdAt'
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'RecognitionComposer',

  mixins: [mixin],

  props: {
    showCancel: Boolean,
    initialAccountIds: Array,
    initialMessage: String,
    submitButtonText: String,
    rows: String,
    Number,
  },

  components: {
    MessageComposer,
  },

  data() {
    return {
      initialMentionedAccounts: [],
      initialContent: '',
      accountSuggestions: [],
      isSearchingAccounts: false,
      isLoading: false,
    }
  },

  watch: {
    initialAccountIds: {
      immediate: true,
      async handler(newVal) {
        if (!newVal?.length) return
        this.isLoading = true
        await this.getInitialAccounts()
        this.isLoading = false
      },
    },
  },

  async mounted() {
    await this.searchAccounts()
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    filteredAccountSuggestions() {
      return this.accountSuggestions.filter(
        account => account.id !== this.accountId
      )
    },
  },

  methods: {
    async getInitialAccounts() {
      try {
        this.isSearchingAccounts = true

        const response = await api.account.getAccounts(this.organizationId, {
          filters: { id: this.initialAccountIds },
        })

        const initialAccounts = response?.data?.results ?? []

        this.accountSuggestions = _.uniqBy(
          [...this.accountSuggestions, ...initialAccounts],
          '_id'
        )

        this.setInitialComposerState(initialAccounts)
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSearchingAccounts = false
      }
    },

    async setInitialComposerState(initialAccounts) {
      this.initialMentionedAccounts = initialAccounts
      this.initialContent = ''
      await this.$nextTick()
      this.initialContent =
        this.initialMessage ||
        initialAccounts.map(account => `@${account._id}`).join(' ') + ' '
    },

    async searchAccounts(terms) {
      try {
        this.isSearchingAccounts = true

        const response = await api.account.searchPeople(this.organizationId, {
          filters: { isDeleted: false, status: 'joined' },
          terms,
          sort: {
            by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT,
            order: 'desc',
          },
        })

        this.accountSuggestions =
          response?.data?.results?.map?.(result => result.account) ?? []
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSearchingAccounts = false
      }
    },

    handleSubmit(recognition) {
      this.$emit('submit', recognition)
    },

    validator({ accountIds }) {
      if (_.isEmpty(accountIds)) {
        this.showError(this.$t('recognitions.select_someone'))
        return false
      }
      return true
    },
  },
}
</script>
